import React, { useMemo } from 'react'
import { CalendarEventBlock, sortEventsLongestFirst } from './CalendarEventBlock'
import {
  formatTime,
  getCurrentTimeIndicatorTop,
  getEventsForDate,
  hourSlots,
  isSameDay,
  formatDayViewSubtitle,
} from './dateUtils'
import { AvailabilityCalendarEvent, AvailabilitySchedule } from './types'
import {
  DayColumn,
  DayTimeGrid,
  DayViewHeader,
  DayViewName,
  DayViewNumber,
  TimeGridWrapper,
  TimeIndicator,
  TimeLabel,
  ViewPanel,
} from './style'

interface DayViewProps {
  date: Date
  events: AvailabilityCalendarEvent[]
  schedule: AvailabilitySchedule
}

export const DayView = ({ date, events, schedule }: DayViewProps): JSX.Element => {
  const hours = useMemo(
    () => hourSlots(schedule.hourStart, schedule.hourEnd),
    [schedule.hourStart, schedule.hourEnd]
  )
  const today = useMemo(() => new Date(), [])
  const isToday = isSameDay(date, today)
  const dayEvents = useMemo(() => getEventsForDate(events, date), [events, date])
  const indicatorTop = getCurrentTimeIndicatorTop(
    today,
    schedule.hourStart,
    schedule.hourEnd
  )

  return (
    <ViewPanel>
      <DayViewHeader>
        <DayViewNumber $today={isToday}>{date.getDate()}</DayViewNumber>
        <DayViewName>{formatDayViewSubtitle(date)}</DayViewName>
      </DayViewHeader>

      <TimeGridWrapper>
        <DayTimeGrid>
          {hours.map(hour => {
            const hourEvents = sortEventsLongestFirst(
              dayEvents.filter(event => event.start.getHours() === hour)
            )
            return (
              <React.Fragment key={hour}>
                <TimeLabel $isFirst={hour === schedule.hourStart}>
                  <span>{formatTime(hour)}</span>
                </TimeLabel>
                <DayColumn>
                  {hourEvents.map(event => (
                    <CalendarEventBlock
                      key={event.id}
                      event={event}
                      showSpaceLabel
                      inset
                    />
                  ))}
                </DayColumn>
              </React.Fragment>
            )
          })}
          {isToday && indicatorTop != null && (
            <TimeIndicator $top={indicatorTop} />
          )}
        </DayTimeGrid>
      </TimeGridWrapper>
    </ViewPanel>
  )
}
